import React, { useEffect } from 'react';
import { setPageTitle } from '../../utils/title';
import { trackEvent } from '../../utils/analytics';
import { useCart } from '../../context/CartContext';
import Breadcrumb from '../../components/Breadcrumb';
import encryptionImage from '../../assets/images/solutions/security/devsecops-website-encryption-keys.png';
import systemsHardeningImage from '../../assets/images/solutions/security/devsecops-website-systems-hardening.png';
import vulnerabilityManagementImage from '../../assets/images/solutions/security/devsecops-website-vulnerability-management.png';
import networkSegmentationImage from '../../assets/images/solutions/security/devsecops-website-network-segmentation.png';
import securityMonitoringImage from '../../assets/images/solutions/security/devsecops-website-security-monitoring.png';
import incidentResponseImage from '../../assets/images/solutions/security/devsecops-website-incident-response-process.png';
import './Solutions.scss';

const SecuritySolutions = () => {
  const { addToCart, removeFromCart, isInCart } = useCart();

  useEffect(() => {
    setPageTitle('Cloud Infrastructure Security');
  }, []);

  const handleCartAction = (solutionType, title) => {
    const itemId = `security-${solutionType}`;
    const isItemInCart = isInCart(itemId);

    if (isItemInCart) {
      trackEvent('remove_from_cart', 'Security Solutions', {
        solution_name: 'Cloud Infrastructure Security',
        solution_type: solutionType,
        source_page: 'security_solutions',
      });
      removeFromCart(itemId);
    } else {
      trackEvent('add_to_cart', 'Security Solutions', {
        solution_name: 'Cloud Infrastructure Security',
        solution_type: solutionType,
        source_page: 'security_solutions',
      });
      addToCart({
        id: itemId,
        title,
        type: 'Security Solution',
      });
    }
  };

  const breadcrumbItems = [
    { path: '/', label: 'Home' },
    { path: '/solutions', label: 'Solutions' },
    { path: '/solutions/security', label: 'Cloud Infrastructure Security' },
  ];

  const renderCartButton = (solutionType, title) => {
    const itemId = `security-${solutionType}`;
    const isItemInCart = isInCart(itemId);

    return (
      <button
        className="card__link card__link--cart"
        onClick={() => handleCartAction(solutionType, title)}
      >
        {isItemInCart ? 'Remove from shopping cart' : 'Add to shopping cart'}
      </button>
    );
  };

  return (
    <div className="solutions-page">
      <Breadcrumb items={breadcrumbItems} />

      <section className="security-solutions section section--light">
        <div className="container">
          <div className="section__header">
            <p className="section__subtitle">Cloud Security Posture Management, CSPM</p>
            <h1 className="section__title">Cloud Infrastructure Security</h1>
            <p className="section__description">
              We design and implement secure cloud infrastructure that help keep your data
              protected, and systems – secure and compliant with rigorous regulatory standards (SOC
              2, HIPAA, ISO, GDPR...)
            </p>
          </div>

          <div className="solutions__grid">
            <div className="card solution-card">
              <div className="card__image-container">
                <img src={encryptionImage} alt="Data Encryption" className="card__image" />
              </div>
              <h3 className="card__title">Data Encryption</h3>
              <p className="card__description">
                We apply industry-standard encryption protocols to all of your data – cloud storage,
                databases, disk volumes, to ensure its protection "at-rest" (AWS KMS encryption) and
                "in-transit" (SSL, TLS certificates).
              </p>
              {renderCartButton('data_encryption', 'Data Encryption')}
            </div>

            <div className="card solution-card">
              <div className="card__image-container">
                <img src={systemsHardeningImage} alt="Systems Hardening" className="card__image" />
              </div>
              <h3 className="card__title">Systems Hardening</h3>
              <p className="card__description">
                We implement OS and container image hardening pipelines that include OS cleanup,
                security patching (CIS Benchmarks, DISA STIGs), and implementation of strict SELinux
                policies to block execution of unwanted software.
              </p>
              {renderCartButton('systems_hardening', 'Systems Hardening')}
            </div>

            <div className="card solution-card">
              <div className="card__image-container">
                <img
                  src={vulnerabilityManagementImage}
                  alt="Vulnerability Management"
                  className="card__image"
                />
              </div>
              <h3 className="card__title">Vulnerability Management</h3>
              <p className="card__description">
                We establish SIEM, enable CloudTrail, organize thorough audit logging, and set up
                continuous security and malware scanning and regular patching of your fleet of
                virtual machine instances and related AWS cloud services.
              </p>
              {renderCartButton('vulnerability_management', 'Vulnerability Management')}
            </div>

            <div className="card solution-card">
              <div className="card__image-container">
                <img
                  src={networkSegmentationImage}
                  alt="Network Segmentation"
                  className="card__image"
                />
              </div>
              <h3 className="card__title">Network Segmentation</h3>
              <p className="card__description">
                To enable efficient security incident response and increase data protection levels,
                we segment your cloud network into separate VPCs (VLANs), providing an isolation
                layer for each of your application workloads.
              </p>
              {renderCartButton('network_segmentation', 'Network Segmentation')}
            </div>

            <div className="card solution-card">
              <div className="card__image-container">
                <img
                  src={securityMonitoringImage}
                  alt="Security Monitoring"
                  className="card__image"
                />
              </div>
              <h3 className="card__title">Security Monitoring</h3>
              <p className="card__description">
                We set up, configure, and fine-tune eXtended Detection and Response (XDR) solutions,
                such as OSS Wazuh, in your AWS cloud environment, to establish efficient security
                monitoring with timely incident detection and alerting.
              </p>
              {renderCartButton('security_monitoring', 'Security Monitoring')}
            </div>

            <div className="card solution-card">
              <div className="card__image-container">
                <img src={incidentResponseImage} alt="Incident Response" className="card__image" />
              </div>
              <h3 className="card__title">Incident Response</h3>
              <p className="card__description">
                We help your team establish efficient, automated security and operational incident
                response process designed to ensure swift issue containment and deliver automated
                actions to safeguard the integrity of your data.
              </p>
              {renderCartButton('incident_response', 'Incident Response')}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SecuritySolutions;
